import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  PlusIcon, PencilSimpleIcon, MagnifyingGlassIcon,
  ArrowClockwiseIcon, CheckCircleIcon, XCircleIcon,
  ArrowRightIcon, ShieldCheckIcon, ProhibitIcon,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import SearchablePicker from "@/components/SearchablePicker";
import { useRbac } from "@/lib/rbac";

// Phase H1-β · Product-wise RM Alternate Approval Master
// SOURCE RM → APPROVED ALTERNATE RM. Directional. Product-specific.

const today = () => new Date().toISOString().slice(0, 10);
const inpCls = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
const lblCls = "text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block";
const btnPrimary = "inline-flex items-center gap-1.5 h-9 px-4 bg-[var(--accent)] text-white text-xs font-mono uppercase tracking-widest rounded-sm hover:opacity-90 transition disabled:opacity-40 disabled:cursor-not-allowed";
const btnGhost = "inline-flex items-center gap-1.5 h-9 px-4 border border-[var(--border)] text-xs font-mono uppercase tracking-widest rounded-sm hover:bg-[var(--bg-2)] transition disabled:opacity-40 disabled:cursor-not-allowed";

const emptyForm = () => ({
  id: "",
  product_id: "",
  source_rm_id: "",
  alternate_rm_id: "",
  priority: 1,
  development_validation_ref: "",
  effective_from: today(),
  valid_until: "",
  notes: "",
});

const statusPill = (status, currently_valid) => {
  if (status === "Draft") {
    return <span className="text-[9px] px-2 py-0.5 rounded-full border font-mono uppercase bg-zinc-500/15 text-zinc-300 border-zinc-500/40">Draft</span>;
  }
  if (status === "Inactive") {
    return <span className="text-[9px] px-2 py-0.5 rounded-full border font-mono uppercase bg-red-500/15 text-red-300 border-red-500/40">Withdrawn</span>;
  }
  if (currently_valid) {
    return <span className="text-[9px] px-2 py-0.5 rounded-full border font-mono uppercase bg-emerald-500/15 text-emerald-300 border-emerald-500/40">Approved · Valid</span>;
  }
  return <span className="text-[9px] px-2 py-0.5 rounded-full border font-mono uppercase bg-amber-500/15 text-amber-300 border-amber-500/40">Approved · Out of window</span>;
};

export default function RmAlternateApprovals() {
  const { hasPerm } = useRbac();
  const canCreate = hasPerm("rm_alternate:create");
  const canEdit = hasPerm("rm_alternate:edit");
  const canApprove = hasPerm("rm_alternate:approve");
  const canWithdraw = hasPerm("rm_alternate:unapprove");

  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [rms, setRms] = useState([]);
  const [q, setQ] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [validOnly, setValidOnly] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const [withdrawFor, setWithdrawFor] = useState(null);
  const [withdrawReason, setWithdrawReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: r }, { data: prods }, { data: rmList }] = await Promise.all([
        api.get("/rm-alternate-approvals/", {
          params: {
            ...(filterStatus ? { status: filterStatus } : {}),
            ...(validOnly ? { currently_valid: true } : {}),
          },
        }),
        api.get("/items/").catch(() => ({ data: [] })),
        api.get("/raw-materials/").catch(() => ({ data: [] })),
      ]);
      setRows(r || []);
      setProducts((prods || []).filter(p => p.is_active !== false));
      setRms((rmList || []).filter(m => m.is_active !== false));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStatus, validOnly]);

  const openNew = () => { setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (row) => {
    setForm({
      id: row.id,
      product_id: row.product_id,
      source_rm_id: row.source_rm_id,
      alternate_rm_id: row.alternate_rm_id,
      priority: row.priority || 1,
      development_validation_ref: row.development_validation_ref || "",
      effective_from: row.effective_from || today(),
      valid_until: row.valid_until || "",
      notes: row.notes || "",
    });
    setDialogOpen(true);
  };

  const canSave = form.product_id && form.source_rm_id && form.alternate_rm_id
    && form.source_rm_id !== form.alternate_rm_id && Number(form.priority) >= 1;

  const save = async (e) => {
    e.preventDefault();
    if (!canSave) {
      toast.error(form.source_rm_id === form.alternate_rm_id
        ? "Source and Alternate RM must be different"
        : "Product, Source RM, Alternate RM and Priority are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        product_id: form.product_id,
        source_rm_id: form.source_rm_id,
        alternate_rm_id: form.alternate_rm_id,
        priority: Number(form.priority),
        development_validation_ref: form.development_validation_ref || "",
        effective_from: form.effective_from || "",
        valid_until: form.valid_until || "",
        notes: form.notes || "",
      };
      if (form.id) {
        await api.put(`/rm-alternate-approvals/${form.id}`, {
          priority: payload.priority,
          development_validation_ref: payload.development_validation_ref,
          effective_from: payload.effective_from,
          valid_until: payload.valid_until,
          notes: payload.notes,
        });
        toast.success("Draft updated");
      } else {
        await api.post("/rm-alternate-approvals/", payload);
        toast.success("Draft added");
      }
      setDialogOpen(false); await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const approve = async (row) => {
    if (!confirm(`Approve ${row.approval_no}?\nSOURCE ${row.source_rm_code_live} → ALTERNATE ${row.alternate_rm_code_live}`)) return;
    try {
      await api.post(`/rm-alternate-approvals/${row.id}/approve`, {});
      toast.success(`${row.approval_no} approved`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    }
  };

  const openWithdraw = (row) => {
    setWithdrawFor(row); setWithdrawReason("");
  };

  const doWithdraw = async () => {
    if (!withdrawReason.trim()) { toast.error("Reason is required"); return; }
    try {
      await api.post(`/rm-alternate-approvals/${withdrawFor.id}/withdraw`, {
        reason: withdrawReason.trim(),
      });
      toast.success(`${withdrawFor.approval_no} withdrawn`);
      setWithdrawFor(null); setWithdrawReason("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Withdraw failed");
    }
  };

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return rows;
    return rows.filter(r =>
      (r.approval_no || "").toLowerCase().includes(n) ||
      (r.product_code_live || r.product_code || "").toLowerCase().includes(n) ||
      (r.source_rm_code_live || r.source_rm_code || "").toLowerCase().includes(n) ||
      (r.alternate_rm_code_live || r.alternate_rm_code || "").toLowerCase().includes(n) ||
      (r.notes || "").toLowerCase().includes(n));
  }, [rows, q]);

  const counts = useMemo(() => ({
    draft: rows.filter(r => r.status === "Draft").length,
    approved: rows.filter(r => r.status === "Approved").length,
    valid: rows.filter(r => r.currently_valid).length,
    inactive: rows.filter(r => r.status === "Inactive").length,
  }), [rows]);

  return (
    <div className="p-6 space-y-4" data-testid="rm-alternate-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline">Product Masters</div>
          <h1 className="font-display text-2xl mt-1">Product-wise RM Alternate Approval</h1>
          <div className="text-xs text-zinc-500 mt-1">
            Directional, product-specific approvals: <span className="font-mono text-[var(--accent)]">SOURCE RM → APPROVED ALTERNATE RM</span>.
            Withdrawal is via canonical <span className="font-mono">unapprove</span>. Priority is a non-unique preference.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="rma-refresh" onClick={load} className={btnGhost} disabled={loading}>
            <ArrowClockwiseIcon size={12} weight="bold" />Refresh
          </button>
          {canCreate && (
            <button data-testid="rma-new" onClick={openNew} className={btnPrimary}>
              <PlusIcon size={12} weight="bold" />New Approval
            </button>
          )}
        </div>
      </div>

      <div className="tactical-card p-3 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4 text-[10px] font-mono uppercase tracking-widest">
          <span className="text-zinc-500">Total · <span className="text-white">{rows.length}</span></span>
          <span className="text-zinc-300">Draft · {counts.draft}</span>
          <span className="text-emerald-300">Approved · {counts.approved}</span>
          <span className="text-emerald-300">Valid Today · {counts.valid}</span>
          <span className="text-red-300">Withdrawn · {counts.inactive}</span>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="rma-filter-status" value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="h-8 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs font-mono">
            <option value="">All statuses</option>
            <option value="Draft">Draft only</option>
            <option value="Approved">Approved only</option>
            <option value="Inactive">Withdrawn only</option>
          </select>
          <label className="flex items-center gap-1.5 cursor-pointer text-[10px] font-mono uppercase tracking-widest" data-testid="rma-valid-only">
            <input type="checkbox" checked={validOnly} onChange={e => setValidOnly(e.target.checked)} className="accent-[var(--accent)]" />
            Valid today
          </label>
          <div className="relative">
            <MagnifyingGlassIcon size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="rma-search" value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search approval / product / RM…"
              className="h-8 w-72 pl-7 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]"
            />
          </div>
        </div>
      </div>

      <div className="tactical-card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
            <tr>
              <th className="px-3 py-2 text-left w-40">Approval No.</th>
              <th className="px-3 py-2 text-left">Product</th>
              <th className="px-3 py-2 text-left">Source RM</th>
              <th className="px-3 py-2 text-center w-6">→</th>
              <th className="px-3 py-2 text-left">Approved Alternate RM</th>
              <th className="px-3 py-2 text-center w-14">Priority</th>
              <th className="px-3 py-2 text-left w-24">Eff. From</th>
              <th className="px-3 py-2 text-left w-24">Valid Until</th>
              <th className="px-3 py-2 text-center w-32">Status</th>
              <th className="px-3 py-2 text-right w-40">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={10} className="text-center py-10 text-xs text-zinc-500">
                {rows.length ? "No matches" : 'No approvals yet — click "New Approval" to add one'}
              </td></tr>
            )}
            {filtered.map((r, i) => (
              <tr key={r.id} data-testid={`rma-row-${i}`} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/40">
                <td className="px-3 py-2 font-mono text-xs text-[var(--accent)]">{r.approval_no}</td>
                <td className="px-3 py-2 text-[11px]">
                  <div className="font-mono">{r.product_code_live || r.product_code || "—"}</div>
                  <div className="text-[10px] text-zinc-500 truncate max-w-[220px]">{r.product_description_live || ""}</div>
                </td>
                <td className="px-3 py-2 text-[11px]">
                  <div className="font-mono">{r.source_rm_code_live || r.source_rm_code || "—"}</div>
                  <div className="text-[10px] text-zinc-500 truncate max-w-[180px]">{r.source_rm_description_live || ""}</div>
                </td>
                <td className="px-3 py-2 text-center text-[var(--accent)]"><ArrowRightIcon size={14} weight="bold" /></td>
                <td className="px-3 py-2 text-[11px]">
                  <div className="font-mono">{r.alternate_rm_code_live || r.alternate_rm_code || "—"}</div>
                  <div className="text-[10px] text-zinc-500 truncate max-w-[180px]">{r.alternate_rm_description_live || ""}</div>
                </td>
                <td className="px-3 py-2 text-center font-mono text-xs">{r.priority}</td>
                <td className="px-3 py-2 font-mono text-[10px] text-zinc-400">{r.effective_from || "—"}</td>
                <td className="px-3 py-2 font-mono text-[10px] text-zinc-400">{r.valid_until || <span className="text-zinc-600">∞</span>}</td>
                <td className="px-3 py-2 text-center">{statusPill(r.status, r.currently_valid)}</td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {r.status === "Draft" && canEdit && (
                      <button data-testid={`rma-edit-${i}`} onClick={() => openEdit(r)} className="p-1 text-zinc-400 hover:text-white" title="Edit Draft">
                        <PencilSimpleIcon size={14} weight="bold" />
                      </button>
                    )}
                    {r.status === "Draft" && canApprove && (
                      <button data-testid={`rma-approve-${i}`} onClick={() => approve(r)} className="p-1 text-emerald-400 hover:text-emerald-200" title="Approve">
                        <ShieldCheckIcon size={14} weight="bold" />
                      </button>
                    )}
                    {r.status === "Approved" && canWithdraw && (
                      <button data-testid={`rma-withdraw-${i}`} onClick={() => openWithdraw(r)} className="p-1 text-red-400 hover:text-red-200" title="Withdraw">
                        <ProhibitIcon size={14} weight="bold" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="rma-dialog" className="bg-[var(--bg-1)] border-[var(--border)] max-w-2xl" description="Create or edit an RM alternate approval draft.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ShieldCheckIcon size={16} weight="bold" className="text-[var(--accent)]" />
              {form.id ? "Edit Draft Approval" : "New Alternate Approval"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className={lblCls}>Product *</label>
              <SearchablePicker
                testid="rma-product"
                value={form.product_id}
                onChange={(v) => setForm({ ...form, product_id: v })}
                placeholder="Select product…"
                headerHint="Search by code or description"
                disabled={!!form.id}
                options={products.map(p => ({ value: p.id, code: p.code || "", name: p.description || p.article_no || "" }))}
              />
              {form.id && <div className="mt-1 text-[10px] font-mono text-zinc-500">Locked — Product is fixed once created.</div>}
            </div>
            <div>
              <label className={lblCls}>Source RM *</label>
              <SearchablePicker
                testid="rma-source-rm"
                value={form.source_rm_id}
                onChange={(v) => setForm({ ...form, source_rm_id: v })}
                placeholder="Select source RM…"
                headerHint="Search by code or description"
                disabled={!!form.id}
                options={rms.map(m => ({ value: m.id, code: m.code || "", name: m.description || "" }))}
              />
            </div>
            <div>
              <label className={lblCls}>Approved Alternate RM *</label>
              <SearchablePicker
                testid="rma-alt-rm"
                value={form.alternate_rm_id}
                onChange={(v) => setForm({ ...form, alternate_rm_id: v })}
                placeholder="Select alternate RM…"
                headerHint="Search by code or description"
                disabled={!!form.id}
                options={rms.filter(m => m.id !== form.source_rm_id)
                  .map(m => ({ value: m.id, code: m.code || "", name: m.description || "" }))}
              />
            </div>
            <div>
              <label className={lblCls}>Priority (lower = preferred)</label>
              <input data-testid="rma-priority" type="number" min="1" step="1" value={form.priority}
                onChange={e => setForm({ ...form, priority: e.target.value })} className={inpCls + " font-mono"} required />
            </div>
            <div>
              <label className={lblCls}>Development / Validation Ref</label>
              <input data-testid="rma-dv-ref" value={form.development_validation_ref}
                onChange={e => setForm({ ...form, development_validation_ref: e.target.value })}
                placeholder="Trial no · QA report · Sample approval…"
                className={inpCls + " font-mono text-xs"} />
              <div className="text-[10px] text-zinc-500 mt-1">Required at approval time.</div>
            </div>
            <div>
              <label className={lblCls}>Effective From</label>
              <input data-testid="rma-eff-from" type="date" value={form.effective_from}
                onChange={e => setForm({ ...form, effective_from: e.target.value })} className={inpCls + " font-mono"} />
              <div className="text-[10px] text-zinc-500 mt-1">Required at approval time.</div>
            </div>
            <div>
              <label className={lblCls}>Valid Until <span className="text-zinc-600 normal-case">(blank = open-ended)</span></label>
              <input data-testid="rma-valid-until" type="date" value={form.valid_until}
                onChange={e => setForm({ ...form, valid_until: e.target.value })} className={inpCls + " font-mono"} />
            </div>
            <div className="md:col-span-2">
              <label className={lblCls}>Notes</label>
              <textarea data-testid="rma-notes" value={form.notes} rows={2}
                onChange={e => setForm({ ...form, notes: e.target.value })}
                className={inpCls + " h-auto py-2 text-xs"}
                placeholder="Optional context (approving authority, trial context, restrictions…)" />
            </div>
          </form>
          <DialogFooter>
            <button className={btnGhost} onClick={() => setDialogOpen(false)} disabled={saving} data-testid="rma-cancel">
              <XCircleIcon size={12} weight="bold" />Cancel
            </button>
            <button data-testid="rma-save" className={btnPrimary} onClick={save} disabled={!canSave || saving}>
              <CheckCircleIcon size={12} weight="bold" />{saving ? "Saving…" : (form.id ? "Update Draft" : "Save Draft")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Withdraw dialog */}
      <Dialog open={!!withdrawFor} onOpenChange={(o) => { if (!o) { setWithdrawFor(null); setWithdrawReason(""); } }}>
        <DialogContent data-testid="rma-withdraw-dialog" className="bg-[var(--bg-1)] border-[var(--border)] max-w-lg" description="Withdraw an approved RM alternate.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ProhibitIcon size={16} weight="bold" className="text-red-400" />
              Withdraw {withdrawFor?.approval_no}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-zinc-400">
              <div className="font-mono">{withdrawFor?.source_rm_code_live} → {withdrawFor?.alternate_rm_code_live}</div>
              <div className="text-[10px] text-zinc-500 mt-1">Product · {withdrawFor?.product_code_live}</div>
            </div>
            <div>
              <label className={lblCls}>Reason *</label>
              <textarea data-testid="rma-withdraw-reason" value={withdrawReason} rows={3}
                onChange={e => setWithdrawReason(e.target.value)}
                className={inpCls + " h-auto py-2 text-xs"}
                placeholder="Explain why this approval is being withdrawn…" required />
            </div>
            <div className="text-[10px] text-zinc-500">
              Withdrawal marks the approval Inactive but preserves history. A fresh Draft may be created on the same triple afterwards.
            </div>
          </div>
          <DialogFooter>
            <button className={btnGhost} onClick={() => { setWithdrawFor(null); setWithdrawReason(""); }} data-testid="rma-withdraw-cancel">
              Cancel
            </button>
            <button data-testid="rma-withdraw-confirm" className={btnPrimary + " bg-red-600 hover:bg-red-500"}
              onClick={doWithdraw} disabled={!withdrawReason.trim()}>
              Withdraw
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
